import { t } from "@lingui/macro";
import { experienceHasWrittenWork, isRealExperience } from "@reactive-resume/schema";
import { Button } from "@reactive-resume/ui";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { trackFirstRealExperienceSaved } from "@/client/libs/analytics";
import { useDialog } from "@/client/stores/dialog";
import { useResumeStore } from "@/client/stores/resume";

export const FirstRunPrompt = () => {
  const [searchParams] = useSearchParams();
  const { open } = useDialog("experience");
  const resumeId = useResumeStore((state) => state.resume.id);
  const experience = useResumeStore((state) => state.resume.data.sections.experience.items);
  const tracked = useRef(false);
  const [dismissed, setDismissed] = useState(false);

  const isOnboarding = searchParams.get("onboarding") === "1";
  const hasReal = experience.some((item) => isRealExperience(item.company));
  const hasDraftedWork = experience.some((item) => experienceHasWrittenWork(item.summary));

  useEffect(() => {
    if (!isOnboarding || !hasReal || tracked.current) return;
    tracked.current = true;
    trackFirstRealExperienceSaved(resumeId);
    const timer = window.setTimeout(() => setDismissed(true), hasDraftedWork ? 10_000 : 1600);
    return () => window.clearTimeout(timer);
  }, [hasDraftedWork, hasReal, isOnboarding, resumeId]);

  useEffect(() => {
    if (!isOnboarding) return;
    const timer = window.setTimeout(() => {
      document
        .getElementById("experience")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 400);
    return () => window.clearTimeout(timer);
  }, [isOnboarding]);

  if (!isOnboarding || dismissed) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        className="rounded-xl border border-primary/25 bg-primary/5 px-3.5 py-3"
      >
        {hasReal ? (
          <p className="text-sm font-medium">
            {hasDraftedWork
              ? t`We wrote a first draft from your jobs. Open a role to rewrite it, or chat with AI on the right.`
              : t`That's a real resume. Keep going with summary and skills next.`}
          </p>
        ) : (
          <div className="space-y-3">
            <p className="text-sm leading-relaxed">
              {t`Your name is on the page. Add one real job next. That's what hiring managers read first.`}
            </p>
            <Button
              size="sm"
              className="w-full"
              onClick={() => open("create", { id: "experience" })}
            >
              {t`Add a role`}
            </Button>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};
